import { Badge, Button, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Database, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { usePOSContext } from "../context/POSContext";
import {
  getReferenceCacheStaleness,
  OFFLINE_SYNC_CHANGED_EVENT,
} from "../lib/offline-sync/db";
import {
  getCachedReferenceManifest,
  invalidatePosReferenceQueries,
  isReferenceCacheRefreshInProgress,
  referenceCacheManifestKey,
  refreshPosReferenceCache,
} from "../lib/offline-sync/reference-cache";
import type { ReferenceCacheManifest } from "../lib/offline-sync/types";

const MAX_AGE_MS = 12 * 60 * 60 * 1000;

function formatCachedAt(value?: string): string {
  if (!value) return "ຍັງບໍ່ມີ cache";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "cache date ບໍ່ຖືກ";
  return `cache ${date.toLocaleTimeString("lo-LA", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function ReferenceCacheStatusBadge() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? undefined;
  const queryClient = useQueryClient();
  const [manifest, setManifest] = useState<ReferenceCacheManifest | null>(null);
  const [stale, setStale] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );

  useEffect(() => {
    if (!branchId) {
      setManifest(null);
      setStale(true);
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      const [nextManifest, staleness] = await Promise.all([
        getCachedReferenceManifest(branchId),
        getReferenceCacheStaleness(
          referenceCacheManifestKey(branchId),
          MAX_AGE_MS,
        ),
      ]);
      if (cancelled) return;
      setManifest(nextManifest ?? null);
      setStale(staleness.stale);
    };

    void refresh();
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(OFFLINE_SYNC_CHANGED_EVENT, refresh);
    };
  }, [branchId]);

  useEffect(() => {
    const updateOnline = () =>
      setOnline(typeof navigator === "undefined" || navigator.onLine);

    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);

  if (!branchId) return null;

  const hasErrors = (manifest?.errors.length ?? 0) > 0;
  const label = hasErrors
    ? "cache warning"
    : stale
      ? "cache stale"
      : formatCachedAt(manifest?.refreshedAt);

  const offline = !online;

  const handleRefresh = async () => {
    if (offline) {
      toast.error("ຕ້ອງ online ກ່ອນ refresh cache");
      return;
    }
    if (isReferenceCacheRefreshInProgress(branchId)) {
      return;
    }
    setRefreshing(true);
    try {
      const nextManifest = await refreshPosReferenceCache({
        branchId,
        deviceId,
        includeVariantDetails: true,
        maxAgeMs: MAX_AGE_MS,
        force: true,
      });
      setManifest(nextManifest);
      setStale(false);
      await invalidatePosReferenceQueries(queryClient);
      toast.success("Refresh cache ສຳເລັດ");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Refresh cache ບໍ່ສຳເລັດ",
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="hidden items-center gap-1 lg:inline-flex">
      <Badge
        variant={hasErrors || stale ? "secondary" : "default"}
        className="items-center gap-1"
        title={
          hasErrors
            ? manifest?.errors[0]?.message
            : formatCachedAt(manifest?.refreshedAt)
        }
      >
        <Database className="h-3 w-3" />
        {label}
      </Badge>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={
          offline || refreshing || isReferenceCacheRefreshInProgress(branchId)
        }
        title={offline ? "Offline: refresh cache ບໍ່ໄດ້" : "Refresh cache"}
        onClick={handleRefresh}
      >
        <RefreshCw
          className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
        />
      </Button>
    </div>
  );
}
